import { ReactNode, useEffect, useState } from 'react';
import { motion } from 'framer-motion';

import MotionDiv from '@/components/common/Motion/MotionDiv';
import MotionItem from './MotionItem';

import { ExpenseItem } from '@/services/api/expense';
import { formatCurrency } from '@/utils/currency';
import { IncomeItem } from '@/services/api/income';
import { LOCALE_MAP } from '@/i18n';

import { PiCaretDown, PiCaretUp, PiTrash } from 'react-icons/pi';

export interface ListItem {
  id: string;
  title: string;
  subtitle?: string;
  value?: string | number | ReactNode;
  badge?: ReactNode;
  icon?: ReactNode;
  groupId: string;
  EmptyItem?: ReactNode;
  props?: {
    className?: string
    titleClassName?: string
    onClick?: () => void
  }
}

interface Group {
  id: string;
  name: string;
  description: string;
  badge?: ReactNode;
  open?: boolean | undefined;
  EmptyGroup?: ReactNode;
  friction?: number;
  childrenFriction?: number;
  setOpen?: (open: boolean) => void;
  onDelete?: () => void;
  props?: { className?: string };
}

interface MotionListProps {
  items: ListItem[];
  groups: Group[];
  className?: string;
}

export const formatDateGroup = (dateString: string, locale: string = 'lt') => {
  const date = new Date(dateString)

  return date.toLocaleDateString(LOCALE_MAP[locale], {
    month: 'long',
  })
}

interface GetAmountGroupsOptions {
  showDescription?: boolean
  locale?: string
}

export const getAmountGroups = (
  items: ExpenseItem[] | IncomeItem[],
  { showDescription = true, locale = 'lt' }: GetAmountGroupsOptions = {}
) => {
  const uniqueGroups = Array.from(
    new Set(items.map(item => formatDateGroup(item.date, locale)))
  )

  return uniqueGroups.map((groupId, index) => {
    const groupItems = items.filter(item => formatDateGroup(item.date, locale) === groupId)
    const groupTotal = groupItems.reduce((sum, item) => sum + item.amount, 0)

    return {
      id: groupId,
      name: groupId[0].toUpperCase() + groupId.slice(1),
      description: showDescription && groupTotal > 0 ? formatCurrency.format(groupTotal) : '',
      open: index < 1,
      childrenFriction: 0.02,
      props: {
        className: "border-b border-zinc-200 pb-4"
      }
    }
  })
}

const getDelay = (
  groupIndex: number,
  itemIndex: number,
  friction: number = 0.05
): number => {
  return (groupIndex * 2 + itemIndex) * friction;
};

export const MotionList = ({
  items,
  groups,
  className = ''
}: MotionListProps) => {
  const [openedGroups, setOpenedGroups] = useState<string[]>([]);

  const groupsKey = JSON.stringify(groups.map(group => group.id))

  useEffect(() => {
    setOpenedGroups(groups
      .filter((group) => group.open)
      .map((group) => group.id))
  }, [groupsKey])

  const groupedItems = items.reduce((acc, item) => {
    if (!acc[item.groupId]) {
      acc[item.groupId] = [];
    }
    acc[item.groupId].push(item);
    return acc;
  }, {} as Record<string, ListItem[]>);

  const toggleGroup = (groupId: string) => {
    setOpenedGroups((prev) => {
      if (prev.includes(groupId)) {
        return prev.filter((id) => id !== groupId);
      } else {
        return [...prev, groupId];
      }
    });
  };

  /* TODO: make the opening logic read better */
  return (
    <MotionDiv className={`flex flex-col gap-8 ${className}`}>
      {groups.map((group, groupIndex) => {
        const groupItems = groupedItems[group.id] || [];

        const isOpenEnabled = group.open !== undefined;
        const isOpen = openedGroups.includes(group.id);

        return (
          <MotionDiv
            key={group.id}
            className="space-y-6"
            delay={getDelay(groupIndex, 0, group.friction)}
          >
            <motion.div
              whileTap={{ scale: 0.98 }}
              className={`flex flex-row justify-between ${group.props?.className}`}
              onClick={() => toggleGroup(group.id)}>
              <div className='flex flex-row items-center gap-2'>
                {group.onDelete && (
                  <PiTrash
                    className='text-zinc-500'
                    onClick={(e) => {
                      e.stopPropagation();
                      group.onDelete?.();
                    }}
                  />
                )}
                {group.name && <h3 className="font-semibold text-sm text-zinc-700">
                  {group.name}
                </h3>}
                {group.badge}
              </div>
              <div className='flex flex-row items-center gap-2'>
                {group.description && <span className='text-sm text-zinc-700'>
                  {group.description}
                </span>}
                {isOpenEnabled && <>{isOpen ? <PiCaretUp /> : <PiCaretDown />}</>}
              </div>
            </motion.div>
            {(!isOpenEnabled || isOpen) && (
              <div className="space-y-7">
                {groupItems.length === 0 && group.EmptyGroup}
                {groupItems.map((item, itemIndex) => {
                  if (!item.value && item.EmptyItem) {
                    const EmptyItem = item.EmptyItem
                    return <div key={item.id}>{EmptyItem}</div>
                  }

                  return (
                  <MotionDiv
                    key={item.id}
                    delay={getDelay(groupIndex, itemIndex + 1, group.childrenFriction || 0.1)}
                  >
                    <MotionItem
                      icon={item.icon}
                      title={item.title}
                      subtitle={item.subtitle}
                      value={item.value}
                      badge={item.badge}
                      group={item.groupId}
                      {...item.props}
                    />
                  </MotionDiv>
                )})}
              </div>
            )}
          </MotionDiv>
        );
      })}
    </MotionDiv>
  );
};

export default MotionList;